import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Linkify from "react-linkify";
import PropTypes from "prop-types";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { makeStyles } from '@material-ui/core/styles';
import FacebookIcon from '@material-ui/icons/Facebook';
import InstagramIcon from '@material-ui/icons/Instagram';
import LanguageIcon from '@material-ui/icons/Language';
import { IconButton, Grid, Typography, Box, Paper } from '@material-ui/core';

import { getResizedImageUrl } from "../../utils/imageManager";
import Image from "../common/Image";
import JobDetailLocation from "../job/JobDetail/JobDetailLocation";
import client from "../../utils/client";
import noImageUrl from "../../img/no_image.jpg";
import Spinner from "../common/Spinner";
import CurrentJobCard from "./BusinessProfileDetail/CurrentJobCard";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '20rem',
  },
  profileSection: {
    minHeight: '20rem',
    padding: theme.spacing(1),
    margin: theme.spacing(1.5),
  },
  avatarContainer: {
    width: '100%',
    height: '40rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#0a0a0a',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      height: '30rem',
    },
  },
  avatar: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: '100%',
    margin: 'auto',
  },
  heading: {
    fontSize: '3rem',
    marginBottom: theme.spacing(4),
    textAlign: 'center',
    color: '#066298',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2.4rem',
    },
  },
  contentContainer: {
    margin: theme.spacing(2),
  },
  flexResponsive: {
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  introduction: {
    flexGrow: 1,
    whiteSpace: 'pre-wrap',
    margin: theme.spacing(1),
    '& a': {
      textDecoration: 'underline',
      color: 'blue',
    },
  },
  imagesContainer: {
    maxWidth: '40rem',
    padding: theme.spacing(2),
    backgroundColor: '#fffceb',
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
  },
  imageGallery: {
    '& .image-gallery-slide img': {
      height: '60rem',
      objectFit: 'contain',
    },
  },
  socialSection: {
    margin: theme.spacing(1),
  },
  socialTitle: {
    marginBottom: theme.spacing(1),
    fontSize: '1.7rem',
    fontWeight: 700,
  },
  socialContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  addressSection: {
    margin: theme.spacing(1),
  },
  addressTitle: {
    marginBottom: theme.spacing(1),
    fontSize: '1.7rem',
    fontWeight: 700,
  },
  locationIcon: {
    color: '#4a4a4a',
    fontSize: '1.2rem',
    marginRight: theme.spacing(1),
    verticalAlign: 'middle',
  },
  socialIcon: {
    fontSize: '2.4rem',
    '&:hover': {
      opacity: 0.8,
    },
  },
  facebookIcon: {
    color: '#1877f2',
    fontSize: '2.4rem',
    '&:hover': {
      opacity: 0.8,
    },
  },
  instagramIcon: {
    color: '#e4405f',
    fontSize: '2.4rem',
    '&:hover': {
      opacity: 0.8,
    },
  },
  websiteIcon: {
    color: '#3273dc',
    fontSize: '2.4rem',
    '&:hover': {
      opacity: 0.8,
    },
  },
  iconButton: {
    padding: theme.spacing(1),
  },
  jobListContainer: {
    margin: theme.spacing(2),
  },
  noJobsMessage: {
    margin: theme.spacing(2),
    fontSize: '1.6rem',
    color: theme.palette.text.secondary,
  },
  boxSub: {
    backgroundColor: '#fff7d6',
    borderRadius: '10px',
    border: 'solid 2px #f5f5f5',
    padding: theme.spacing(1),
  },
  jobDetailGallery: {
    '& .image-gallery-slide': {
      backgroundColor: '#0a0a0a',
      border: 'solid 1px #0a0a0a',
    },
    '& .image-gallery-image': {
      textAlign: 'center',
    },
    '& .image-gallery-image img': {
      margin: 'auto',
      objectFit: 'contain',
      width: '100%',
      height: '40rem',
      [theme.breakpoints.down('sm')]: {
        height: '35rem',
      },
    },
  },
}));

const BusinessProfileDetail = props => {
  // const { name, profile } = props.user;
  const userId = props.match.params.id;
  const [userDetails, setDetails] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [jobList, setJobList] = useState([]);
  const [loadingJobList, setLoadingJobList] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    setDetails(null);
    setJobList([]);
    setLoadingProfile(true);
    setLoadingJobList(true);
    if (userId) {
      client
        .get("user/" + userId)
        .then(result => {
          setDetails(result.data);
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          setLoadingProfile(false);
        });

      client
        .get("job/user/" + userId)
        .then(result => {
          setJobList(result.data);
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          setLoadingJobList(false);
        });

      client.patch(`user/${userId}/viewCount`);
    }
  }, [userId, setDetails, setLoadingJobList, setJobList, setLoadingProfile]);

  const renderJobList = () => {
    if (jobList.length === 0) {
      return (
        <Typography className={classes.noJobsMessage}>
          現在募集中の求人はありません
        </Typography>
      );
    }

    return (
      <Grid container spacing={2}>
        {jobList.map(job => (
          <Grid item xs={12} md={6} key={job._id}>
            <Link
              to={`/jobs/${job._id}`}
            >
              <CurrentJobCard job={job} />
            </Link>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderProfiles = () => {
    if (!userDetails) return <div>No data</div>;

    const { name, profile } = userDetails;

    const renderSocialLinks = () => {
      const links = [];

      if (profile.companyWebsite) {
        links.push(
          <IconButton
            key="website"
            href={profile.companyWebsite}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.iconButton}
          >
            <LanguageIcon className={classes.websiteIcon} />
          </IconButton>
        );
      }

      if (profile.facebook) {
        links.push(
          <IconButton
            key="facebook"
            href={profile.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.iconButton}
          >
            <FacebookIcon className={classes.facebookIcon} />
          </IconButton>
        );
      }

      if (profile.instagram) {
        links.push(
          <IconButton
            key="instagram"
            href={profile.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.iconButton}
          >
            <InstagramIcon className={classes.instagramIcon} />
          </IconButton>
        );
      }

      return links;
    };

    return (
      <Box>
        {profile.avatar ? (
          <Box className={classes.avatarContainer}>
            <Image
              src={profile.avatar ? profile.avatar.image_url : noImageUrl}
              alt="avatar"
              className={classes.avatar}
            />
          </Box>
        ) : null}
        <Typography variant="h2" className={classes.heading}>
          {name}
        </Typography>
        <Box className={classes.contentContainer}>
          <Box className={classes.flexResponsive}>
            <Box className={classes.introduction}>
              <Linkify>{profile.introduction}</Linkify>
            </Box>
            {profile.images && profile.images.length > 1 ? (
              <Box className={classes.imagesContainer}>
                {renderImages(profile.images)}
              </Box>
            ) : null}
          </Box>
          {profile.companyWebsite ? (
            <Box className={classes.socialSection}>
              <Typography variant="h4" className={classes.socialTitle}>
                ウェブサイト
              </Typography>
              <Box className={classes.socialContainer}>
                {renderSocialLinks()}
              </Box>
            </Box>
          ) : null}
          {profile.address ? (
            <Box className={classes.addressSection}>
              <Typography variant="h4" className={classes.addressTitle}>
                住所
              </Typography>
              <JobDetailLocation address={profile.address} />
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  };

  const renderImages = images => {
    let items = [];

    images.forEach(image => {
      items.push({
        original: image.image_url,
        thumbnail: getResizedImageUrl(image.image_url, "c_thumb,w_200")
      });
    });

    return (
      <Box className={classes.jobDetailGallery}>
        <ImageGallery
          items={items}
          autoPlay={true}
          slideInterval={6000}
          showNav={false}
          showFullscreenButton={false}
          showPlayButton={false}
        />
      </Box>
    )
  };

  return (
    <Box className={classes.root}>
      <Box component="section" className={classes.profileSection}>
        {loadingProfile ? <Spinner /> : renderProfiles()}
      </Box>
      <Box component="section" className={classes.jobListContainer}>
        <Typography variant="h3" className="section-title">
          現在募集中の求人
        </Typography>
        {loadingJobList ? <Spinner /> : renderJobList()}
      </Box>
    </Box>
  );
};

BusinessProfileDetail.propTypes = {
  // user: PropTypes.object
  id: PropTypes.number
};

export default BusinessProfileDetail;

